import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { SitesService } from '@shared/services/sites/sites.service';

import { HttpClient } from '@angular/common/http';
import { ACL_SITE_ACTIONS, AuthService } from '@shared/services/auth.service';

import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { ColumnMode, DatatableComponent } from '@siemens/ngx-datatable';
import _ from 'lodash';

import {
  ADD_ROUTE,
  ADMIN_CLUSTER_LOGS_ROUTE,
  MY_LOGS_ROUTE,
  MY_SITES_ROUTE,
  PRINT_ROUTE,
} from 'app/app-routing.constants';
import {
  ActionEnum,
  Cause,
  CountryGraphType,
  DateHistograms,
  DrillDown,
  LogRequest,
  LogsService,
} from '@shared/services/logs/logs.service';
import { RANGE_PRESETS } from '@shared/services/calendar-data.service';
import { Donut, DonutType, DonutTypes } from '@shared/components/highcharts/donut/donut';
import {
  computeInterval,
  Graph,
  RESPONSE_TIME,
  TOP_URLS_RESPONSE_TIME_SUM,
  TrafficHistogramType,
  TRAFIC,
} from '@shared/components/highcharts/graph/graph';
import { CountryClickEvent, WorldMapComponent } from '@shared/components/highcharts/world-map/world-map.component';
import { SiteSelectorComponent } from '@shared/components/site-selector/site-selector.component';
import { DateRange } from '@shared/utils/date-range';
import { firstValueFrom, Subscription } from 'rxjs';
import { BaseDashboardComponent } from './baseDashboardComponent';
import { Filter, FiltersEnum, formatFiltersLogs } from '../my-logs/filters/filters';
import { FiltersComponent } from '../my-logs/filters/filters.component';
import { OrganizationSiteService } from '@shared/services/organization-site.service';
import { TrackEvent } from './track.event';
import Highcharts from 'highcharts';
import moment from 'moment/moment';
import { ADMIN_CONTEXT, BaseLogRequest } from '@shared/models/ContextSiteModel';
import { BaseQueryParamsShape, QueryParamsService } from '@shared/services/query-params-service/query-params.service';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import { SiteSelectorComponent as SiteSelectorComponent_1 } from '../../shared/components/site-selector/site-selector.component';
import { FeatureFlagDirective, NotFeatureFlagDirective } from '@shared/utils/feature-flag.directive';
import { NgClass, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { DashboardCardComponent } from './dashboard-card/dashboard-card.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { DonutComponent } from '@shared/components/highcharts/donut/donut.component';
import { GraphComponent } from '@shared/components/highcharts/graph/graph.component';
import { BlockedIPsTableComponent } from './blocked-ips-table/blocked-ips-table.component';
import { SlowRequestsTableComponent } from './slow-requests-table/slow-requests-table.component';
import { MostFrequentRequestsTableComponent } from './most-frequent-requests-table/most-frequent-requests-table.component';
import { DurationSecondsPipe, TranslateLabelPipe } from '@shared/pipe/filter.pipe';

type QueryParamsShape = BaseQueryParamsShape & {
  filters: string | null;
};

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    SiteSelectorComponent_1,
    FeatureFlagDirective,
    FiltersComponent,
    NgIf,
    NotFeatureFlagDirective,
    RouterLink,
    DashboardCardComponent,
    SpinnerComponent,
    DonutComponent,
    GraphComponent,
    WorldMapComponent,
    NgClass,
    NgSwitch,
    NgSwitchCase,
    BlockedIPsTableComponent,
    SlowRequestsTableComponent,
    MostFrequentRequestsTableComponent,
    DurationSecondsPipe,
    TranslatePipe,
    TranslateLabelPipe,
  ],
})
export class DashboardComponent extends BaseDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('siteSelector') siteSelector!: SiteSelectorComponent;
  @ViewChild('filtersComponent') filtersComponent: FiltersComponent;

  @ViewChild('myTable') set datatable(table: DatatableComponent) {
    if (table) {
      table.columnMode = ColumnMode.force; // make resizing table header working
    }
  }

  @ViewChild(DatatableComponent)
  ngxDatatable: DatatableComponent;
  breakdown: { [key in Exclude<TrafficHistogramType, 'traffic'>]: Highcharts.Options };
  isLoading = true;
  isTableLoading = false;

  ActionEnum = ActionEnum;

  chartInterval: number;
  pageSelected = Table.TOP_BLOCKED_IPS;
  originSelected: CountryGraphType = 'traffic';

  ADMIN_CONTEXT = ADMIN_CONTEXT;

  filters: Filter[] = [];
  activeFilters: Filter[] = [];

  private subscriptions: Subscription = new Subscription();

  private hasInitialized = false;

  private isSyncingFilters = false;

  private pendingRefresh: Promise<void> | null = null;

  get isPrintable(): boolean {
    return (
      this.ctx != ADMIN_CONTEXT &&
      !this.isLoading &&
      this.sites[this.ctx]?.store.sites.length > 0 &&
      this.filters.length === 0
    );
  }

  get isAdminClusterView(): boolean {
    return this.ctx == ADMIN_CONTEXT;
  }

  get isASiteSelected(): number {
    return this.sites[this.ctx].current.selectedSites.length;
  }

  get isAClusterSelected(): boolean {
    return !!this.sites[this.ctx].current.cluster;
  }

  get isNewOrga(): boolean {
    return (
      this.ctx != ADMIN_CONTEXT &&
      !this.isLoading &&
      this.auth.canProv() &&
      (this.sites[this.ctx]?.store.sitesList ?? []).length == 0
    );
  }

  private get isQueryParamsEnabled(): boolean {
    return this.featureFlagService.featureEnabled('dashboardQueryParams');
  }

  private get canHandleChildEvent(): boolean {
    return this.hasInitialized && !this.isSyncingFilters;
  }

  constructor(
    http: HttpClient,
    logsService: LogsService,
    sites: SitesService,
    private siteApi: OrganizationSiteService,
    private queryParamsService: QueryParamsService,
    private router: Router,
    private route: ActivatedRoute,
    private featureFlagService: FeatureFlagService,
    auth: AuthService,
    graph: Graph,
    donut: Donut,
    private cd: ChangeDetectorRef,
  ) {
    super(http, sites, logsService, auth, graph, donut);
  }

  ngOnInit() {
    if (this.route.snapshot.data.adminClusterView) {
      this.ctx = ADMIN_CONTEXT;
    }
  }

  async ngAfterViewInit() {
    // add scroll bar to ng-select inputs
    const ngSelects = document.querySelectorAll('.ng-select-container');

    ngSelects.forEach((s) => {
      s.classList.add('custom-scrollbar');
    });

    await this.sites.load(this.ctx);

    await new Promise<void>((resolve) => setTimeout(resolve, 0));

    this.subscriptions.add(this.route.queryParamMap.subscribe((params) => this.onUrlChange(params)));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onSiteSelectionChanged() {
    if (!this.canHandleChildEvent) return;
    this.setQueryParams();
    this.refresh();
  }

  onPeriodEmitted(range: DateRange) {
    this.sites[this.ctx].current.period.range = range;
    if (!this.canHandleChildEvent) return;
    this.setQueryParams();
    this.refresh();
  }

  selectSite(siteName: string) {
    this.sites.setSites(this.ctx, [siteName]);
    this.setQueryParams();
    this.refresh();
  }

  onFiltersChange(filters: Filter[]) {
    if (!this.canHandleChildEvent) return;
    this.filters = filters;
    this.updateActiveFilters();
    this.queryParamsService.storeFilters(this.ctx, this.queryParamsService.serializeFiltersToUrl(this.filters));
    this.setQueryParams();
    this.refresh();
    this.cd.detectChanges();
  }

  async getDateHistogram(request: BaseLogRequest): Promise<DateHistograms<TrafficHistogramType, BySite>> {
    const res = await super.getDateHistogram(request);
    this.breakdown = _.mapValues(_.pick(res, DonutTypes), (v, donutType: DonutType) =>
      this.donut.formatBreakdown(v.bySite, donutType),
    );
    return res;
  }

  async getOrigineAttaques(request: BaseLogRequest) {
    await super.getOrigineAttaques(request);
    if (this.sites[this.ctx]?.current?.selectedSites?.length == 1) {
      const siteDetail = await firstValueFrom(
        this.siteApi.getSite(this.sites[this.ctx].current.selectedSites[0].domainName),
      );
      this.countryDatas['blacklisted'] = siteDetail.blacklistedCountries.map((countryCode) => [countryCode, 0]);
    }
  }

  async getTopBlockedIps(request: BaseLogRequest) {
    await super.getTopBlockedIps(request);

    const siteAccessRights = _.fromPairs(
      await Promise.all(
        _(this.topBlockedIps)
          .map((row) => row.site)
          .uniq()
          .map(async (site) => [site, (await this.sites.getAccessRights(site)).includes(ACL_SITE_ACTIONS.EDIT)])
          .value(),
      ),
    );
    return this.topBlockedIps.forEach((row) => (row['canWhitelistIp'] = siteAccessRights[row.site]));
  }

  private async onUrlChange(params: ParamMap): Promise<void> {
    if (
      this.isQueryParamsEnabled &&
      this.hasInitialized &&
      this.queryParamsService.paramsMatchCurrentState(params, this.buildQueryParams())
    ) {
      return;
    }

    const filtersParam = params.get('filters');

    if (filtersParam) {
      this.filters = await this.queryParamsService.deserializeFiltersFromUrl(filtersParam, this.filtersComponent);
      this.queryParamsService.storeFilters(this.ctx, filtersParam);
    } else {
      const stored = this.queryParamsService.getStoredFilters(this.ctx);
      this.filters = stored
        ? await this.queryParamsService.deserializeFiltersFromUrl(stored, this.filtersComponent)
        : [];
    }

    this.updateActiveFilters();
    this.syncFiltersToComponent();

    this.queryParamsService.applyQueryParams(params, this.ctx, !!filtersParam);

    const capped = this.capRangeTo30Days();

    if (!this.hasInitialized || capped) {
      this.setQueryParams(true);
    }

    this.hasInitialized = true;
    await this.refresh();
  }

  refresh(): Promise<void> {
    if (!this.pendingRefresh) {
      this.pendingRefresh = Promise.resolve().then(async () => {
        this.pendingRefresh = null;
        this.sites[this.ctx].refreshPeriod();
        this.queryParamsService.storeState(this.ctx, this.queryParamsService.buildQueryParams(this.ctx));
        await this.loadData();
      });
    }
    return this.pendingRefresh;
  }

  async loadData() {
    if (!this.sites[this.ctx].current.cluster?.uid) {
      console.warn('*** loadData called with no cluster selected');
      this.blockAttack = {};
      this.audience = {};
      this.breakdown = { analyzed: null, robot: null, suspect: null, blocked: null };
      this.totals = { analyzed: 0, robot: 0, suspect: 0, blocked: 0, traffic: 0 };
      this.countryDatas = { traffic: null, blockedByBrain: null, blockedCountry: null, robot: null };
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    try {
      const request = this.getRequestData();
      this.chartInterval = computeInterval(this.sites[this.ctx].current.period.range);

      await Promise.all([
        this.getBlockAttack(request),
        this.getDateHistogram(request),
        this.getOrigineAttaques(request),
      ]);

      // Bottom tables
      if (!this.isAdminClusterView || this.isASiteSelected) {
        this.topBlockedIps = null;
        this.cpuConsumingRequests = null;
        this.slowRequests = null;
        this.loadTableData(this.pageSelected);
      }
    } finally {
      this.isLoading = false;
      if (this.sites[this.ctx].current.period.autoRefresh) this.siteSelector.onDataLoaded();
      else this.siteSelector.clearRefreshInterval();
    }
  }

  onCountryClick(graphType: CountryGraphType, event: CountryClickEvent) {
    const additionalFilters: { [prop in CountryGraphType]?: { [FiltersEnum.CAUSE]: Cause } } = {
      blockedCountry: { cause: 'geoBlocked' },
      blockedByBrain: { cause: 'analyzedKO' },
    };

    this.navigateToLogs({ [FiltersEnum.COUNTRY]: event.countryCode, ...additionalFilters[graphType] });
  }

  onAttackCategoryClick(event: DrillDown) {
    this.navigateToLogs({
      [FiltersEnum.ATTACK_CATEGORY]: event.uid,
      [FiltersEnum.ACTION]: ActionEnum.BLOCKED,
    });
  }

  private buildQueryParams(): QueryParamsShape {
    if (!this.isQueryParamsEnabled) {
      return;
    }
    return {
      ...this.queryParamsService.buildQueryParams(this.ctx),
      filters: this.queryParamsService.serializeFiltersToUrl(this.filters),
    };
  }

  private setQueryParams(replaceUrl = false) {
    if (!this.isQueryParamsEnabled) {
      return;
    }
    const queryParams = this.buildQueryParams();
    this.queryParamsService.storeState(this.ctx, queryParams);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl,
      queryParamsHandling: 'merge',
    });
  }

  private updateActiveFilters(): void {
    this.activeFilters = this.filters.filter((f) => f.active);
  }

  private syncFiltersToComponent() {
    if (!this.filtersComponent) return;
    this.isSyncingFilters = true;
    try {
      this.filtersComponent.filters = this.filters.reduce((acc, f) => acc.set(f.filterDefinition.key, f), new Map());
    } finally {
      this.isSyncingFilters = false;
    }
  }

  onPeriodEmittedFromZoneSelected(range: DateRange) {
    this.sites[this.ctx].current.period.autoRefresh = false;
    this.onPeriodEmitted(range);
  }

  changeOrigine(type: CountryGraphType) {
    this.originSelected = type;
  }

  async selectTable(table: Table) {
    this.pageSelected = table;
    if (this.isAClusterSelected) {
      if (_.isNil(this[table])) {
        await this.loadTableData(table);
      }
    }
  }

  async loadTableData(t: Table) {
    this.isTableLoading = true;

    const request = this.getRequestData();
    switch (t) {
      case Table.TOP_BLOCKED_IPS:
        await this.getTopBlockedIps(request);
        break;
      case Table.CPU_CONSUMING_REQUESTS:
        await this.getTopConsumingRequest(request);
        break;
      case Table.SLOW_REQUESTS:
        await this.getSlowRequest(request);
        break;
    }

    return (this.isTableLoading = false);
  }

  private capRangeTo30Days(): boolean {
    const period = this.sites[this.ctx].current.period;
    if (period.range.length('day') > 30) {
      period.range = RANGE_PRESETS['30d'];
      period.autoRefresh = false;
      return true;
    }
    return false;
  }

  navigateToLogsFilteredByAction(action: string) {
    this.navigateToLogs({ [FiltersEnum.ACTION]: action });
  }

  navigateToLogsFilteredByIp(event: { ip: string; site: string }) {
    this.navigateToLogs({
      [FiltersEnum.IP]: event.ip,
      sites: [event.site],
    });
  }

  navigateToLogsFilteredByCountry(event: { countryCode: string }) {
    this.navigateToLogs({
      [FiltersEnum.COUNTRY]: event.countryCode,
    });
  }

  clickSlowRequestPath(event: { site: string; path: string; avg: number }) {
    this.navigateToLogs({
      [FiltersEnum.RESPONSE_TIME_GTE]: Math.trunc(event.avg),
      [FiltersEnum.PATH]: event.path,
      graphType: RESPONSE_TIME,
      sites: [event.site],
    });
  }

  clickFrequentRequestPath(event: { site: string; path: string; avg: number }) {
    this.navigateToLogs({
      [FiltersEnum.RESPONSE_TIME_GTE]: Math.trunc(event.avg),
      [FiltersEnum.PATH]: event.path,
      graphType: RESPONSE_TIME,
      graphSubType: TOP_URLS_RESPONSE_TIME_SUM,
      sites: [event.site],
    });
  }

  navigateToLogs(filters: TrackEvent) {
    const defaultParams: TrackEvent = {
      clusterName: this.sites[this.ctx].current.cluster.name,
      graphType: 'traffic',
    };
    this.router.navigate([this.isAdminClusterView ? ADMIN_CLUSTER_LOGS_ROUTE : MY_LOGS_ROUTE], {
      state: { ...defaultParams, ...filters },
    });
  }

  toPrint() {
    const current = this.sites[this.ctx].current;
    this.router.navigate([PRINT_ROUTE], {
      state: {
        cluster: current.cluster,
        siteNames: current.selectedSites.map((s) => s.domainName),
        tags: [current.tag],
      },
      queryParams: {
        start: current.period.range.start.valueOf(),
        end: current.period.range.end.valueOf(),
        cluster: current.cluster.uid,
      },
    });
  }

  private getRequestData(): LogRequest {
    return { ...this.sites[this.ctx].buildRequestData(), ...formatFiltersLogs(this.activeFilters) };
  }

  protected readonly TRAFIC = TRAFIC;
  protected readonly BLOCKED_COUNTRY = 'blockedCountry';
  protected readonly Table = Table;
  protected readonly MY_SITES_ROUTE = MY_SITES_ROUTE;
  protected readonly ADD_ROUTE = ADD_ROUTE;
  protected readonly moment = moment;
}

export type CountryData = [string, number];

export type BySite = { bySite: { [prop: string]: number } };

enum Table {
  TOP_BLOCKED_IPS = 'topBlockedIps',
  CPU_CONSUMING_REQUESTS = 'cpuConsumingRequests',
  SLOW_REQUESTS = 'slowRequests',
}
